import { useState } from 'react'

import { Building2, Check, ChevronDown } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useAuth } from '@/features/auth/auth-context'
import { DropdownItem, DropdownMenu } from '@/shared/components/ui/dropdown-menu'

export function OrganizationSwitcher() {
  const { user, switchOrganization, refreshUser } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [switching, setSwitching] = useState(false)

  const organizations = user?.organizations ?? []
  if (organizations.length < 2) {
    return null
  }

  const activeId = user?.active_organization_id ?? user?.organization_id ?? null
  const active = organizations.find((organization) => organization.id === activeId)

  const handleSelect = async (organizationId: string, close: () => void) => {
    if (organizationId === activeId || switching) {
      close()
      return
    }
    setSwitching(true)
    try {
      await switchOrganization(organizationId)
      // Открытый проект принадлежит прежней организации и в новой недоступен.
      if (location.pathname.startsWith('/projects/')) {
        navigate('/projects')
      }
    } catch {
      // Организация ушла из-под пользователя (его исключили, проект передали) —
      // сервер отвечает 403, а устаревший пункт всё ещё висит в шапке.
      // Про ошибку сообщает перехватчик axios, здесь обновляем список.
      await refreshUser().catch(() => {})
    } finally {
      setSwitching(false)
      close()
    }
  }

  return (
    <DropdownMenu
      trigger={
        <span className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm text-[var(--color-text-soft)] hover:bg-[var(--panel-subtle)] hover:text-[var(--color-text)]">
          <Building2 className="h-4 w-4" />
          <span className="hidden max-w-[160px] truncate sm:inline">
            {active?.name ?? 'Организация'}
          </span>
          <ChevronDown className="h-4 w-4" />
        </span>
      }
    >
      {(close) => (
        <div className="min-w-[220px]">
          {organizations.map((organization) => (
            <DropdownItem
              key={organization.id}
              onClick={() => void handleSelect(organization.id, close)}
            >
              <span className="flex w-full items-center justify-between gap-2">
                <span className="truncate">{organization.name}</span>
                {organization.id === activeId ? <Check className="h-4 w-4" /> : null}
              </span>
            </DropdownItem>
          ))}
        </div>
      )}
    </DropdownMenu>
  )
}
